import classNames from 'classnames';
import * as React from 'react';

import * as styles from './ChatListContextMenu.css';
import { useContextMenu } from '../../hooks/useContextMenu';

export type TMenuItem = {
  title: string;
  handler(chatId: string): void;
  isColored?: boolean;
};

type TChatListContextMenuProps = {
  listRef: React.RefObject<HTMLDivElement | null>;
  menuItems: TMenuItem[];
  onShowContextMenu(chatId: string): void;
};

export const ChatListContextMenu: React.FC<TChatListContextMenuProps> = ({ listRef, menuItems, onShowContextMenu }) => {
  const [currentChatId, setCurrentChatId] = React.useState('');

  const onShowContextMenuHandler = React.useCallback(
    (event: MouseEvent): boolean => {
      const chatId = ((event.target as HTMLElement).closest('[data-chatid]') as HTMLElement)?.dataset?.chatid as string;

      if (!chatId) {
        return false;
      }

      setCurrentChatId(chatId);
      onShowContextMenu(chatId);

      return true;
    },
    [onShowContextMenu],
  );

  const onHideContextMenu = React.useCallback(() => {
    setCurrentChatId('');
  }, []);

  const { anchorPoint, show, closeContextMenu } = useContextMenu({
    elementRef: listRef,
    onShowContextMenu: onShowContextMenuHandler,
    onHideContextMenu,
    isEnabled: true,
  });

  React.useEffect(() => {
    document.addEventListener('click', closeContextMenu);

    return () => {
      document.removeEventListener('click', closeContextMenu);
    };
  }, [closeContextMenu]);

  const items = React.useMemo(
    () =>
      menuItems.map(({ title, handler, isColored }, index) => (
        <li
          key={index}
          className={classNames(styles['item'], isColored && styles['item--colored'])}
          onClick={() => {
            handler(currentChatId);
          }}
          data-name="ChatListContextMenuItem"
        >
          {title}
        </li>
      )),
    [menuItems, currentChatId],
  );

  if (!show) {
    return null;
  }

  return (
    <ul className={styles['menu']} style={{ top: anchorPoint.y, left: anchorPoint.x }}>
      {items}
    </ul>
  );
};
